import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import styled from 'styled-components';
import { GLOBAL_NAVIGATION_TOOLBAR_DEFAULT_HEIGHT } from 'nav-meta/constants';
import throttle from 'transmute/throttle';
import Heading from './Heading';
import { useElementWidth } from '../hooks/useElementWidth';
import { CardWrapper } from 'foundations-components/transitional/components/Card';
import MiniRecordAvatarAndName from './MiniRecordAvatarAndName';
import { Flex } from 'foundations-components/components/Flex';
import MiniHighlightActivityButtons from './MiniHighlightActivityButtons';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const GLOBAL_NAV_HEIGHT = parseInt(GLOBAL_NAVIGATION_TOOLBAR_DEFAULT_HEIGHT, 10);
const MINI_HIGHLIGHT_TRANSITION_DURATION_MILLIS = 200;

/**
 * CSS explanation:
 * The mini highlight is rendered inside card with has borders, so we need to offset the position to make fully overlap.
 * Also, other cards shuld not be visible under the mini highlight when scrolling, so we need overlap them and cover with gradient.
 *
 * - width: +2px to account for border width on both sides
 * - transform: translateY(-1px) translateX(-1px) to offset the border width
 * - margin-block-start: -600, padding-block-start: 600 and padding-block-end: 400 to cover space above and below the mini highlight
 * - background: to cover other cards
 */
const MiniHighlightContainer = styled.div.withConfig({
  displayName: "MiniHighlight__MiniHighlightContainer",
  componentId: "sc-1wtzhvb-0"
})(["position:fixed;width:", ";opacity:", ";transition:opacity 200ms cubic-bezier(0.42,0,0.58,1);z-index:2;overflow:hidden;visibility:", ";transform:translateY(-1px) translateX(-1px);margin-block-start:", ";padding-block-start:", ";padding-block-end:", ";background:", ";"], ({
  width
}) => `${width + 2}px`, ({
  visibility
}) => visibility === 'Hidden' ? 0 : 1, ({
  visibility
}) => visibility === 'Hidden' ? 'hidden' : 'visible', ({
  theme
}) => `-${theme.space['600']}`, ({
  theme
}) => theme.space['600'], ({
  theme
}) => theme.space['400'], ({
  theme
}) => `linear-gradient(to bottom, ${theme.color['fill-surface-recessed']} 0%, ${theme.color['fill-surface-recessed']} calc(100% - ${theme.space['700']}), transparent 100%)`);
const MiniHighlight = ({
  highlightContainerRef
}) => {
  const [widthRef, setWidthRef] = useState(null);
  const [visibility, setVisibility] = useState('Hidden');
  const [scrollContainer, setScrollContainer] = useState(null);
  useLayoutEffect(() => {
    if (highlightContainerRef) {
      setScrollContainer(highlightContainerRef.closest('[data-scroll-trackable-container="left-sidebar"]'));
    }
  }, [highlightContainerRef]);
  const handleScroll = useCallback(() => {
    if (!highlightContainerRef) {
      return;
    }
    const {
      bottom
    } = highlightContainerRef.getBoundingClientRect();
    if (bottom <= GLOBAL_NAV_HEIGHT && visibility !== 'Visible') {
      setVisibility('Visible');
    } else if (bottom > GLOBAL_NAV_HEIGHT && visibility === 'Visible') {
      setVisibility('Hiding');
    }
  }, [highlightContainerRef, visibility]);
  useEffect(() => {
    if (!scrollContainer) return;
    const throttledScroll = throttle(50, handleScroll);
    scrollContainer.addEventListener('scroll', throttledScroll);
    return () => {
      scrollContainer.removeEventListener('scroll', throttledScroll);
    };
  }, [handleScroll, scrollContainer]);
  useEffect(() => {
    let timeoutId;
    if (visibility === 'Hiding') {
      timeoutId = setTimeout(() => {
        setVisibility('Hidden');
      }, MINI_HIGHLIGHT_TRANSITION_DURATION_MILLIS);
    }
    return () => {
      clearTimeout(timeoutId);
    };
  }, [visibility]);
  const width = useElementWidth(widthRef);
  return /*#__PURE__*/_jsx("div", {
    ref: setWidthRef,
    children: /*#__PURE__*/_jsx(MiniHighlightContainer, {
      visibility: visibility,
      width: width,
      children: /*#__PURE__*/_jsxs(CardWrapper, {
        children: [/*#__PURE__*/_jsx(Heading, {}), /*#__PURE__*/_jsxs(Flex, {
          paddingInline: "400",
          paddingBlock: "300",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "200",
          children: [/*#__PURE__*/_jsx(MiniRecordAvatarAndName, {}), /*#__PURE__*/_jsx(MiniHighlightActivityButtons, {})]
        })]
      })
    })
  });
};
export default MiniHighlight;